/**
 * Claim flags — the honesty layer.
 *
 * ─────────────────────────────────────────────────────────────────────
 * READ THIS BEFORE FLIPPING ANYTHING TO `true`.
 * ─────────────────────────────────────────────────────────────────────
 *
 * During the 2026 refresh an audit of the site found a number of public
 * claims that nothing in the codebase or the business could substantiate:
 * named testimonials attached to stock photographs of strangers, company
 * performance percentages, and case studies quoting specific revenue
 * figures.
 *
 * They were not deleted — they are all still here, verbatim, so nothing
 * of yours was lost. They are switched off until someone can stand behind
 * them, because an unverifiable number on an agency site is worse than no
 * number: a prospective client who checks one and finds it hollow stops
 * trusting everything else on the page.
 *
 * To publish any of these: confirm it is true, then flip the flag. If a
 * claim is true but the wording overstates it, edit the text rather than
 * leaving it off.
 */

/**
 * Four testimonials from "Sophia P." (TechCorp), "Michael R." (StartUp
 * Ventures), "Jennifer L." (Innovate Inc.) and "David K." (FinTech
 * Solutions), each with a 5-star rating, project name, duration and
 * quoted metrics.
 *
 * ON: the owner confirmed these are real clients and that the quotes and
 * companies are accurate as written.
 *
 * The stock-photo avatars were removed permanently regardless — all four
 * pointed at Unsplash photographs of unrelated strangers being passed off
 * as client headshots, which a reverse image search would have exposed.
 * The component renders initials monograms instead, and there is no
 * `avatar` field left to drop a remote URL back into. To show real faces,
 * add local images to public/images/ and extend the component.
 */
export const SHOW_TESTIMONIALS = true;

/**
 * The "Our Impact" panel: "50+ Projects Delivered", "98% Client
 * Satisfaction", "47% Avg. Growth", "24/7 Support" — plus the headline
 * "Join hundreds of satisfied clients".
 *
 * Why it is off: none of these figures is recorded anywhere, and "hundreds
 * of clients" contradicts "50+ projects" in the same section.
 */
export const SHOW_IMPACT_STATS = false;

/**
 * Case-study cards quoting specific client outcomes — "Scales to 100K
 * Monthly Transactions", "Reconciles $300M+ OTA Commissions
 * Automatically", and the eleven marketing results in
 * data_DigiM.jsx such as "Generated $500k+ in first month".
 *
 * Why it is off: these name real-sounding engagements with hard financial
 * figures and no client, date or evidence attached. This is the single
 * highest-risk category on the site.
 *
 * To turn on: for each one, confirm the client agreed to the figure being
 * published.
 */
export const SHOW_CASE_STUDY_METRICS = false;

/**
 * Hardcoded stats printed identically on every service detail page
 * regardless of which service it is — "45% Efficiency Gain", "98%
 * Uptime", "3-5x ROI Boost", "Secure / Enterprise".
 *
 * Why it is off: the same numbers appeared on all five Business
 * Applications pages and all ten Digital Marketing pages, which makes
 * them decoration rather than data.
 */
export const SHOW_TEMPLATE_STATS = false;

/**
 * "Award-Winning" / "Excellence Recognized" / "industry recognition".
 *
 * Why it is off: no award is named anywhere. Naming the actual award is
 * far more persuasive than the word "award-winning", so if there is one,
 * name it instead of setting this to true.
 */
export const SHOW_AWARD_CLAIMS = false;

/**
 * "24/7 Support" as a advertised service level.
 *
 * Why it is off: this is a support commitment that creates an
 * expectation. Turn it on only if round-the-clock cover genuinely exists.
 * The maintenance and support service page can describe real response
 * times instead.
 */
export const SHOW_247_SUPPORT = false;

/**
 * Public price anchors from the service data — "Starting at $5,000" etc.
 *
 * ON by default: this is the owner's own pricing, deliberately published,
 * and a starting price filters out unqualified enquiries. Set false to
 * move to "request a quote".
 */
export const SHOW_PUBLIC_PRICING = true;

/**
 * The About page's capability figures: "5+ service verticals", "10+
 * skilled professionals", "30+ solution types".
 *
 * ON by default: unlike the impact stats these describe the shape of the
 * business rather than claimed results, and they are consistent with the
 * 25 services and 4 categories that actually exist in the catalog.
 * Confirm the headcount is right.
 */
export const SHOW_TEAM_STATS = true;
